import type { DataGridFilterRenderContext } from '@ibsys/kit';
import { Switch, Tabs, TabsContent, TabsList, TabsTrigger } from '@ibsys/kit';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFilterRangeMode } from './NumericFilterRangeMode';
import { NumericFilterValuesMode } from './NumericFilterValuesMode';
import {
  isNumericValuesFilter,
  type NumericValueLimits,
  toNumericFilterItems,
  toNumericFilterRange,
} from './numericFilterValue';

/** Режим отбора числовой колонки: границами или перечнем */
type NumericFilterMode = 'range' | 'values';

/** Пропсы отбора по числовой колонке */
type Props = NumericValueLimits & {
  /** Контекст фильтра колонки: значение, черновик и применение */
  ctx: DataGridFilterRenderContext;
  /**
   * Показывать признак заполненности — у колонки, значение которой может отсутствовать. Тумблер
   * грида такой колонке не достаётся: рядом со своим полем отбора грид его не рисует.
   */
  enableNotEmptyFilter?: boolean;
};

/**
 * Отбор по числовой колонке в двух режимах: границы «от–до» и перечень значений и промежутков.
 * Действует тот режим, в котором задано условие, — переключение снимает условие прежнего.
 */
export const NumericColumnFilter: FC<Props> = ({ ctx, isIntegerOnly, min, max, enableNotEmptyFilter }) => {
  const { t } = useTranslation('common');
  const isValuesMode = isNumericValuesFilter(ctx.value);
  const items = toNumericFilterItems(ctx.value);
  const range = toNumericFilterRange(isValuesMode ? undefined : ctx.value);
  // Признак заполненности и условие по значению взаимоисключающие: включённый признак отменяет
  // условие, поэтому набирать его, пока признак включён, нельзя
  const isNotEmpty = ctx.operator === 'notEmpty';

  const [mode, setMode] = useState<NumericFilterMode>(isValuesMode ? 'values' : 'range');

  const handleModeChange = (next: string) => {
    if (next === mode) return;
    setMode(next as NumericFilterMode);
    // Условие прежнего режима вместе с ним и снимается: отбор идёт либо по границам, либо перечнем
    ctx.setDraft(undefined);
  };

  return (
    <div className="flex w-64 flex-col gap-2 p-2">
      {enableNotEmptyFilter ? (
        <Switch
          size="sm"
          value={isNotEmpty}
          onChange={(next) => ctx.setOperator(next ? 'notEmpty' : undefined)}
          label={t('dataGrid.filterNotEmpty')}
          className="w-auto"
        />
      ) : null}
      <Tabs value={mode} onValueChange={handleModeChange}>
        <TabsList>
          <TabsTrigger value="range" disabled={isNotEmpty}>
            {t('numericFilter.modeRange')}
          </TabsTrigger>
          <TabsTrigger value="values" disabled={isNotEmpty}>
            {t('numericFilter.modeValues')}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="range" className="mt-2">
          <NumericFilterRangeMode
            range={range}
            onChange={(next) => ctx.setDraft(next)}
            onApply={ctx.apply}
            disabled={isNotEmpty}
          />
        </TabsContent>
        <TabsContent value="values" className="mt-2">
          <NumericFilterValuesMode
            initialItems={items}
            limits={{ isIntegerOnly, min, max }}
            onChange={(next) => ctx.setDraft(next.length ? next : undefined)}
            disabled={isNotEmpty}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
